import React, { useCallback } from 'react';
import { Avatar, Box, Typography, useTheme } from '@mui/material';
import ModalDialog from '../../common/components/ModalDialog';
import { ActiveMember, VerificationText } from '../types/members-types';
import { Nullable } from 'common-types';
import LastAdminOnOrg from './LastAdminOnOrg';
import LastUserOnOrg from './LastUserOnOrg';
import ButtonGroup from '../../common/components/ButtonGroup';
import { setRemoveVerificationValue } from '../store-actions/remove-member-store-actions';

interface IRemoveMemberModalProps {
  open: boolean;
  activeMember: Nullable<ActiveMember>;
  onClose: () => void;
  onRemove: () => void;
  isLoading: boolean;
  verificationValue: string;
  isActiveMemberAdmin: boolean;
  isLastAdminStanding: boolean;
  activeMemberCount: Nullable<number>;
}

const RemoveMemberModal: React.FC<
  React.PropsWithChildren<IRemoveMemberModalProps>
> = props => {
  const theme = useTheme();
  const {
    open,
    onClose,
    activeMember,
    onRemove,
    isLoading,
    verificationValue,
    isActiveMemberAdmin,
    isLastAdminStanding,
    activeMemberCount,
  } = props;

  const handleOnChange = useCallback(event => {
    const { value } = event.target;
    setRemoveVerificationValue(value);
  }, []);

  if (!activeMember) {
    return null;
  }

  const isLastAndActiveMemberAdmin = isLastAdminStanding && isActiveMemberAdmin;
  const isVerificationValue =
    verificationValue === VerificationText.RemoveActiveMember;

  return (
    <ModalDialog
      modalColor={theme.palette.info.main}
      aria-label='Remove Member'
      open={open}
      onClose={onClose}
      modalHeader='Remove Member'>
      <Box flexGrow={1} mb={2}>
        <Typography variant='body2'>
          Are you sure you want to remove the following user from the
          organization ?
        </Typography>
        <Box display='flex' alignItems='center' mt={2} mb={2} padding={1}>
          <Avatar alt={activeMember.name} src={activeMember.picture} />
          <Box display='flex' flexDirection='column' ml={1}>
            <Typography variant='body2'>{activeMember.name}</Typography>
            <Typography variant='body2'>{activeMember.email}</Typography>
          </Box>
        </Box>
      </Box>
      <Box display='flex' alignItems='end'>
        {isLastAndActiveMemberAdmin &&
          (activeMemberCount === 1 ? (
            <LastUserOnOrg
              verificationValue={verificationValue}
              handleOnChange={handleOnChange}
            />
          ) : (
            <LastAdminOnOrg />
          ))}
        <ButtonGroup
          onCancel={onClose}
          onClick={onRemove}
          isLoading={isLoading}
          buttonCancelLabel={'Cancel'}
          buttonCancelTooltip={'Cancel this operation'}
          buttonActionLabel={'Remove'}
          buttonActionTooltip={'Remove member from organization'}
          buttonActionColor={'error'}
          isActionDisabled={isLastAndActiveMemberAdmin && !isVerificationValue}
        />
      </Box>
    </ModalDialog>
  );
};

export default RemoveMemberModal;
